import { useEffect, useMemo, useState, useCallback } from 'react';
import { api } from '../api.js';
import Modal from '../components/Modal.jsx';
import { fmtCents, fmtDateShort, todayIso, addDaysIso, parseAmountToCents } from '../format.js';

export default function Forecast({ state, lookups, mutate, reload, loadFullHistory, showToast }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [horizon, setHorizon] = useState(90);
  const [filterText, setFilterText] = useState('');
  const [busy, setBusy] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [amountTarget, setAmountTarget] = useState(null);
  const [addExpectedOpen, setAddExpectedOpen] = useState(false);

  const today = todayIso();
  const to = useMemo(() => addDaysIso(today, horizon), [today, horizon]);

  const loadForecast = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getForecast(today, to);
      setRows(r);
    } catch (e) {
      showToast({ kind: 'error', message: 'Forecast failed: ' + e.message });
    } finally {
      setLoading(false);
    }
  }, [today, to, showToast]);

  useEffect(() => { loadForecast(); }, [loadForecast]);

  // The engine returns row.date = displayDate (after reschedule) and row.scheduled_on = original.
  // All forecast mutations key off scheduled_on, never the display date.
  const schedOf = (row) => row.scheduled_on ?? row.date;

  function openPay(row) { setPayTarget(row); }
  function openReschedule(row) { setRescheduleTarget(row); }
  function openSetAmount(row) { setAmountTarget(row); }

  async function confirmSetAmount(row, newCents) {
    setBusy(row.id);
    setAmountTarget(null);
    try {
      // newCents === null clears the override back to the rule's projected amount
      await api.setForecastAmount(row.recurring_id, schedOf(row), newCents);
      showToast({
        kind: 'success',
        message: newCents == null
          ? `Restored ${row.item} to projected`
          : `Set ${row.item} to $${(newCents / 100).toFixed(2)}`,
      });
      await loadForecast();
    } catch (e) {
      showToast({ kind: 'error', message: 'Failed: ' + e.message });
    } finally { setBusy(null); }
  }

  async function confirmReschedule(row, newDateIso) {
    setBusy(row.id);
    setRescheduleTarget(null);
    try {
      // newDateIso === schedOf(row) means "clear the override"
      const newOn = newDateIso === schedOf(row) ? null : newDateIso;
      await api.rescheduleForecast(row.recurring_id, schedOf(row), newOn);
      showToast({ kind: 'success', message: newOn ? 'Moved to ' + newOn : 'Restored to original date.' });
      await loadForecast();
    } catch (e) {
      showToast({ kind: 'error', message: 'Failed: ' + e.message });
    } finally { setBusy(null); }
  }

  async function confirmPay(row, actualCents) {
    setBusy(row.id);
    setPayTarget(null);
    try {
      // Always send the entered amount. The worker's payForecastRow uses it
      // as-is when present, so this is just the truth: "log this, with this
      // amount." (Previously we passed null when entered === projected to
      // "save a write," but that branch made it possible for the entered
      // value to never reach the API if something upstream went wrong.)
      const projectedAbs = Math.abs(row.amount_cents);
      const cents = (actualCents != null && actualCents > 0) ? actualCents : projectedAbs;
      await api.payForecast(row.recurring_id, schedOf(row), cents);
      showToast({
        kind: 'success',
        message: cents !== projectedAbs
          ? `Logged ${row.item} at $${(cents/100).toFixed(2)}`
          : `Logged: ${row.item}`,
      });
      await Promise.all([loadForecast(), reload()]);
    } catch (e) {
      showToast({ kind: 'error', message: 'Failed: ' + e.message });
    } finally { setBusy(null); }
  }
  async function skip(row) {
    setBusy(row.id);
    try {
      await api.skipForecast(row.recurring_id, schedOf(row));
      showToast({ kind: 'success', message: 'Skipped: ' + row.item });
      await loadForecast();
    } catch (e) {
      showToast({ kind: 'error', message: 'Failed: ' + e.message });
    } finally { setBusy(null); }
  }
  async function unmark(row) {
    setBusy(row.id);
    try {
      await api.unmarkForecast(row.recurring_id, schedOf(row));
      showToast({ kind: 'success', message: 'Unmarked: ' + row.item });
      await Promise.all([loadForecast(), reload()]);
    } catch (e) {
      showToast({ kind: 'error', message: 'Failed: ' + e.message });
    } finally { setBusy(null); }
  }

  // Compute running balance starting point from the full ledger.
  // The bootstrap ships only the most-recent 50 transactions; without
  // loadFullHistory the starting balance silently undercounts and every
  // projected future balance is anchored to a wrong "today" number.
  useEffect(() => {
    if (state.allTransactions === null && loadFullHistory) {
      loadFullHistory().catch(() => {});
    }
  }, [state.allTransactions, loadFullHistory]);

  const checkingId = state.accounts.find(a => a.name === 'Checking')?.id;
  const balanceToday = useMemo(() => {
    if (!checkingId) return 0;
    const txns = state.allTransactions ?? state.transactions;
    return txns
      .filter(t => t.account_id === checkingId)
      .reduce((sum, t) => sum + t.amount_cents, 0);
  }, [state.allTransactions, state.transactions, checkingId]);

  // Cushion floor — the balance threshold below which the forecast warns.
  // Stored in settings; defaults to $1,000 if unset.
  const cushionFloor = useMemo(() => {
    const v = parseInt(lookups.setting('cushion_floor_cents') ?? '100000', 10);
    return Number.isFinite(v) && v >= 0 ? v : 100000;
  }, [lookups]);

  // Group by date and compute projected running balance for the future.
  // Annotate each row with two flags:
  //   below_floor: running balance after this row is < floor
  //   crosses_floor: this row is the one that took us below (prev was >= floor)
  const grouped = useMemo(() => {
    const out = {};
    let bal = balanceToday;
    for (const r of rows) {
      const before = bal;
      // Ignore rows that already have a transaction (paid). They would double-count.
      if (!r.skipped && !r.paid) bal += r.amount_cents;
      const annotated = {
        ...r,
        running_balance_cents: bal,
        below_floor: bal < cushionFloor,
        crosses_floor: before >= cushionFloor && bal < cushionFloor,
      };
      (out[r.date] ||= []).push(annotated);
    }
    return out;
  }, [rows, balanceToday, cushionFloor]);

  // Earliest dip: first row whose running balance crosses below the floor.
  // (If the user is already under, we still want to flag the row that re-deepens it.)
  const earliestDip = useMemo(() => {
    const all = Object.values(grouped).flat();
    return all.find(r => r.crosses_floor) ?? all.find(r => r.below_floor) ?? null;
  }, [grouped]);

  const lowestPoint = useMemo(() => {
    const all = Object.values(grouped).flat();
    if (!all.length) return null;
    let lowest = all[0];
    for (const r of all) {
      if (r.running_balance_cents < lowest.running_balance_cents) lowest = r;
    }
    return lowest;
  }, [grouped]);

  // ---------------------------------------------------------------
  // Club Tab rollup sub-label (frontend display only)
  // ---------------------------------------------------------------
  // The worker syncs club tab charges from month M into the WF CC
  // forecast row on (M+2)-17 via a forecast_overrides.actual_cents.
  // We re-derive the rollup here purely to render an explanatory
  // sub-label like "incl. $24.22 club tab · May" under that row's
  // amount. No mutations — the override is already applied server-side.
  //
  // Map: scheduled_on (YYYY-MM-17) -> { cents, monthLabel }
  const clubTabAccountId = lookups.setting('club_tab_account_id');
  const clubTabCcRecurringId = lookups.setting('club_tab_cc_recurring_id');
  const clubTabRollups = useMemo(() => {
    const map = {};
    if (!clubTabAccountId || !clubTabCcRecurringId) return map;
    const txns = state.allTransactions ?? state.transactions;
    if (!txns?.length) return map;

    // Bucket club-tab charges by source month "YYYY-MM"
    const monthSums = {};
    for (const t of txns) {
      if (t.account_id !== clubTabAccountId) continue;
      // Charges are negative (expense leg); ignore zero/positive
      if (!t.amount_cents || t.amount_cents >= 0) continue;
      if (!t.occurred_on || t.occurred_on.length < 7) continue;
      const ym = t.occurred_on.slice(0, 7); // YYYY-MM
      monthSums[ym] = (monthSums[ym] ?? 0) + Math.abs(t.amount_cents);
    }

    // Project each source month M into its WF CC due date (M+2)-17
    for (const [ym, cents] of Object.entries(monthSums)) {
      if (cents <= 0) continue;
      const [yStr, mStr] = ym.split('-');
      const y = parseInt(yStr, 10);
      const m = parseInt(mStr, 10); // 1-12 (source month)
      // Add 2 months
      let dueY = y;
      let dueM = m + 2;
      if (dueM > 12) { dueM -= 12; dueY += 1; }
      const dueIso = `${dueY}-${String(dueM).padStart(2, '0')}-17`;
      // Short month label of the SOURCE month (e.g., "May")
      const monthLabel = new Date(Date.UTC(y, m - 1, 1))
        .toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short' });
      map[dueIso] = { cents, monthLabel };
    }
    return map;
  }, [state.allTransactions, state.transactions, clubTabAccountId, clubTabCcRecurringId]);

  // Helper: returns rollup info for a row, or null
  const clubTabInfoFor = (row) => {
    if (!clubTabCcRecurringId) return null;
    if (row.recurring_id !== clubTabCcRecurringId) return null;
    return clubTabRollups[row.scheduled_on] ?? null;
  };

  // Filter: when filterText is set, hide rows whose item doesn't match.
  // Running balances are computed on ALL rows (grouped); we only hide
  // non-matching rows from the render — the balance projection stays correct.
  const filterLower = filterText.trim().toLowerCase();
  const filteredGrouped = useMemo(() => {
    if (!filterLower) return grouped;
    const out = {};
    for (const [date, dateRows] of Object.entries(grouped)) {
      const matching = dateRows.filter(r =>
        r.item?.toLowerCase().includes(filterLower)
      );
      if (matching.length > 0) out[date] = matching;
    }
    return out;
  }, [grouped, filterLower]);
  const filterActive = filterLower.length > 0;
  const filteredCount = filterActive
    ? Object.values(filteredGrouped).flat().length
    : null;

  return (
    <div className="screen">
      <div className="row between" style={{ marginBottom: 12 }}>
        <h1 style={{ margin: 0 }}>Forecast</h1>
        <button className="primary" onClick={() => setAddExpectedOpen(true)}>+ Add expected</button>
      </div>
      <div style={{ marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <select value={horizon} onChange={(e) => setHorizon(parseInt(e.target.value, 10))}
                style={{ flex: '0 0 auto' }}>
          <option value={14}>Next 2 weeks</option>
          <option value={90}>Next 90 days</option>
          <option value={120}>Next 120 days</option>
          <option value={180}>Next 6 months</option>
          <option value={365}>Next 12 months</option>
        </select>
        <div style={{ position: 'relative', display: 'flex' }}>
          <input
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            placeholder="Filter by name…"
            style={{ flex: 1, paddingRight: filterText ? 28 : undefined }}
          />
          {filterText && (
            <button
              onClick={() => setFilterText('')}
              className="ghost"
              aria-label="Clear filter"
              style={{
                position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)',
                padding: '2px 6px', fontSize: 14, lineHeight: 1,
              }}
            >
              ×
            </button>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="muted tiny">Projected balance at end of window</div>
        <div className={'amount ' + (Object.values(grouped).flat().slice(-1)[0]?.running_balance_cents >= cushionFloor ? 'pos' : 'neg')}
             style={{ fontSize: 22, fontWeight: 600 }}>
          {fmtCents(Object.values(grouped).flat().slice(-1)[0]?.running_balance_cents ?? balanceToday)}
        </div>
        <div className="tiny muted" style={{ marginTop: 4 }}>
          Today: {fmtCents(balanceToday)}
        </div>
      </div>

      {earliestDip && (
        <div className="card" style={{
          marginBottom: 16,
          borderColor: 'rgba(220,38,38,0.45)',
          background: 'rgba(220,38,38,0.08)',
        }}>
          <div className="row between" style={{ marginBottom: 6 }}>
            <strong style={{ color: 'var(--red)' }}>⚠ Low balance ahead</strong>
            <span className="tiny muted">floor {fmtCents(cushionFloor)}</span>
          </div>
          <div className="tiny" style={{ marginBottom: 4 }}>
            {earliestDip.crosses_floor
              ? <>Drops below floor on <strong>{fmtDateShort(earliestDip.date)}</strong> after</>
              : <>Already below floor on <strong>{fmtDateShort(earliestDip.date)}</strong> after</>}{' '}
            <strong>{earliestDip.item}</strong> — projected{' '}
            <span className="amount neg">{fmtCents(earliestDip.running_balance_cents)}</span>
          </div>
          {lowestPoint && lowestPoint.id !== earliestDip.id && (
            <div className="tiny muted">
              Lowest point in window: {fmtCents(lowestPoint.running_balance_cents)} on {fmtDateShort(lowestPoint.date)}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="full-spinner"><div className="spinner" /><div>Loading forecast…</div></div>
      ) : Object.keys(filteredGrouped).length === 0 ? (
        <div className="empty">
          <div className="icon">∅</div>
          <div>{filterActive ? `No items matching "${filterText}"` : 'No items in this window'}</div>
          {filterActive && (
            <button className="ghost" style={{ marginTop: 8 }} onClick={() => setFilterText('')}>
              Clear filter
            </button>
          )}
        </div>
      ) : (
        <>
          {filterActive && (
            <div className="tiny muted" style={{ marginBottom: 8 }}>
              {filteredCount} item{filteredCount !== 1 ? 's' : ''} matching &ldquo;{filterText}&rdquo;
              {' · '}running balances reflect full schedule
            </div>
          )}
          {Object.keys(filteredGrouped).sort().map(date => (
            <div key={date} style={{ marginBottom: 16 }}>
              <h2 style={{ margin: '0 0 8px 0' }}>{fmtDateShort(date)}</h2>
              <div className="card" style={{ padding: 0 }}>
                {filteredGrouped[date].map(r => (
                  <ForecastRow key={r.id} row={r} busy={busy === r.id} today={today}
                               clubTabInfo={clubTabInfoFor(r)}
                               onPay={() => openPay(r)} onSkip={() => skip(r)}
                               onUnmark={() => unmark(r)}
                               onReschedule={() => openReschedule(r)}
                               onSetAmount={() => openSetAmount(r)} />
                ))}
              </div>
            </div>
          ))}
        </>
      )}

      <PayConfirmModal
        target={payTarget}
        onCancel={() => setPayTarget(null)}
        onConfirm={confirmPay}
      />

      <RescheduleModal
        target={rescheduleTarget}
        onCancel={() => setRescheduleTarget(null)}
        onConfirm={confirmReschedule}
        schedOf={schedOf}
      />

      <SetAmountModal
        target={amountTarget}
        onCancel={() => setAmountTarget(null)}
        onConfirm={confirmSetAmount}
      />

      <AddExpectedModal
        open={addExpectedOpen}
        onClose={() => setAddExpectedOpen(false)}
        state={state}
        mutate={mutate}
        showToast={showToast}
        onAdded={loadForecast}
      />
    </div>
  );
}

// Types that should NOT trigger a due/overdue alert — money movement or income only
const QUIET_TYPES = new Set(['Savings', 'Transfer', 'Income']);

function ForecastRow({ row, busy, today, clubTabInfo, onPay, onSkip, onUnmark, onReschedule, onSetAmount }) {
  const cls = `list-item forecast-row ${row.paid ? 'paid' : ''} ${row.skipped ? 'skipped' : ''}`;
  const canEditAmount = !row.paid && !row.skipped;
  const showFloorWarning = row.below_floor && !row.paid && !row.skipped;

  // Due/overdue: unpaid, non-quiet type, date is today or in the past
  const isDueToday  = !row.paid && !row.skipped && !QUIET_TYPES.has(row.type) && row.date === today;
  const isOverdue   = !row.paid && !row.skipped && !QUIET_TYPES.has(row.type) && row.date < today;
  const showDueFlag = isDueToday || isOverdue;

  // Build inline style — due/overdue wins visually over low-balance (both can show)
  let rowStyle = {};
  if (showDueFlag) {
    rowStyle = {
      borderLeft: `4px solid ${isOverdue ? 'var(--red)' : 'var(--amber)'}`,
      paddingLeft: 'calc(var(--s3) - 4px)',
      background: isOverdue
        ? 'rgba(220,38,38,0.06)'
        : 'rgba(217,119,6,0.07)',
      outline: isOverdue
        ? '1.5px solid rgba(220,38,38,0.25)'
        : '1.5px solid rgba(217,119,6,0.30)',
      outlineOffset: '-1px',
      borderRadius: 'var(--r2)',
    };
  } else if (showFloorWarning) {
    rowStyle = {
      borderLeft: '3px solid var(--red)',
      paddingLeft: 'calc(var(--s3) - 3px)',
      background: 'rgba(220,38,38,0.04)',
    };
  }

  return (
    <div className={cls} style={rowStyle}>
      <div className="grow">
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {row.item}
          {isDueToday && (
            <span style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
              color: 'var(--amber)', background: 'rgba(217,119,6,0.12)',
              border: '1px solid rgba(217,119,6,0.35)',
              borderRadius: 4, padding: '1px 5px', whiteSpace: 'nowrap',
            }}>
              DUE TODAY
            </span>
          )}
          {isOverdue && (
            <span style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
              color: 'var(--red)', background: 'rgba(220,38,38,0.10)',
              border: '1px solid rgba(220,38,38,0.35)',
              borderRadius: 4, padding: '1px 5px', whiteSpace: 'nowrap',
            }}>
              OVERDUE
            </span>
          )}
        </div>
        <div className="meta">
          {row.type}
          {row.paid && <> · <span className="pill pos">Paid</span></>}
          {row.skipped && <> · <span className="pill warn">Skipped</span></>}
          {row.rescheduled && !row.paid && !row.skipped && (
            <> · <span className="pill info" title={`Originally ${row.scheduled_on}`}>Moved</span></>
          )}
          {row.adjusted && !row.paid && !row.skipped && (
            <> · <span className="pill warn" title="Amount adjusted from projected">Adjusted</span></>
          )}
          {row.crosses_floor && !row.paid && !row.skipped && (
            <> · <span className="pill neg" title="This row drops you below your cushion floor">Below floor</span></>
          )}
          {row.below_floor && !row.crosses_floor && !row.paid && !row.skipped && (
            <> · <span className="pill neg" title="Running balance is below your cushion floor">Low</span></>
          )}
        </div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div className="row" style={{ justifyContent: 'flex-end', gap: 6, alignItems: 'center' }}>
          <span className={'amount ' + (row.amount_cents >= 0 ? 'pos' : 'neg')}>
            {fmtCents(row.amount_cents)}
          </span>
          {canEditAmount && (
            <button
              onClick={onSetAmount}
              className="ghost"
              title="Adjust amount"
              aria-label="Adjust amount"
              style={{ padding: '2px 6px', fontSize: 14, lineHeight: 1 }}
            >
              ✎
            </button>
          )}
        </div>
        {clubTabInfo && clubTabInfo.cents > 0 && (
          <div
            className="tiny muted"
            title={`Includes ${fmtCents(clubTabInfo.cents)} of ${clubTabInfo.monthLabel} club tab charges rolled into this statement`}
            style={{ marginTop: 1, fontSize: '0.72rem', lineHeight: 1.2 }}
          >
            incl. {fmtCents(clubTabInfo.cents)} club tab · {clubTabInfo.monthLabel}
          </div>
        )}
        <div style={{
          fontSize: '0.8rem',
          fontWeight: 600,
          color: row.below_floor ? 'var(--red)' : 'var(--green)',
          marginTop: 1,
        }}>
          → {fmtCents(row.running_balance_cents)}
        </div>
      </div>
      {!row.paid && !row.skipped && (
        <div className="row" style={{ gap: 4 }}>
          <button onClick={onPay} disabled={busy} title="Mark paid">{busy ? <span className="spinner"/> : '✓'}</button>
          <button onClick={onSkip} disabled={busy} title="Skip">×</button>
          <button onClick={onReschedule} disabled={busy} title="Reschedule">⇄</button>
        </div>
      )}
      {(row.paid || row.skipped) && (
        <button onClick={onUnmark} disabled={busy} className="ghost tiny" title="Undo">
          {busy ? <span className="spinner"/> : 'Undo'}
        </button>
      )}
    </div>
  );
}

function PayConfirmModal({ target, onCancel, onConfirm }) {
  // Re-mount when target changes (so the input pre-fills correctly each time)
  if (!target) return null;
  return <PayConfirmInner key={target.id} target={target} onCancel={onCancel} onConfirm={onConfirm} />;
}

function PayConfirmInner({ target, onCancel, onConfirm }) {
  const projectedAbs = Math.abs(target.amount_cents);
  const [amountInput, setAmountInput] = useState((projectedAbs / 100).toFixed(2));
  const isIncome = target.amount_cents >= 0;

  function submit() {
    const cents = parseAmountToCents(amountInput);
    if (cents == null || cents <= 0) {
      // Invalid input → use the projected amount explicitly
      onConfirm(target, projectedAbs);
      return;
    }
    onConfirm(target, cents);
  }

  const projected = fmtCents(target.amount_cents);
  const entered = parseAmountToCents(amountInput);
  const differs = entered != null && entered !== projectedAbs;

  return (
    <Modal
      open={true}
      onClose={onCancel}
      title={`Mark "${target.item}" paid?`}
      footer={
        <>
          <button onClick={onCancel}>Cancel</button>
          <button className="primary" onClick={submit} autoFocus>Confirm</button>
        </>
      }
    >
      <div className="muted tiny" style={{ marginBottom: 12 }}>
        Date: {fmtDateShort(target.date)} · Type: {target.type}
      </div>

      <div className="field">
        <label>{isIncome ? 'Income amount' : 'Amount paid'}</label>
        <input
          inputMode="decimal"
          value={amountInput}
          onChange={(e) => setAmountInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          autoFocus
        />
        <div className="tiny muted" style={{ marginTop: 4 }}>
          Projected: <span className="mono">{projected}</span>
          {differs && (
            <> · <span style={{ color: 'var(--warn)' }}>Logging a different amount</span></>
          )}
        </div>
      </div>
    </Modal>
  );
}

function RescheduleModal({ target, onCancel, onConfirm, schedOf }) {
  if (!target) return null;
  return <RescheduleInner key={target.id} target={target} onCancel={onCancel} onConfirm={onConfirm} schedOf={schedOf} />;
}

function RescheduleInner({ target, onCancel, onConfirm, schedOf }) {
  const [dateInput, setDateInput] = useState(target.date);  // current display date (= actual_on if rescheduled)
  const original = schedOf(target);
  const isRescheduled = !!target.rescheduled;

  function submit() {
    if (!dateInput) return;
    onConfirm(target, dateInput);
  }

  function restore() {
    onConfirm(target, original);
  }

  return (
    <Modal
      open={true}
      onClose={onCancel}
      title="Reschedule"
      footer={
        <>
          <button onClick={onCancel}>Cancel</button>
          <button className="primary" onClick={submit}>Move</button>
        </>
      }
    >
      <div className="muted tiny" style={{ marginBottom: 12 }}>
        {target.item} · {target.type}
      </div>
      <div className="field">
        <label>New Date</label>
        <input
          type="date"
          value={dateInput}
          onChange={(e) => setDateInput(e.target.value)}
        />
        <div className="tiny muted" style={{ marginTop: 4 }}>
          {isRescheduled
            ? <>Originally scheduled for <span className="mono">{original}</span>.</>
            : <>Default: <span className="mono">{original}</span>.</>}
        </div>
      </div>
      {isRescheduled && (
        <button className="ghost tiny" onClick={restore} style={{ marginTop: 8 }}>
          Restore to original date
        </button>
      )}
    </Modal>
  );
}

function SetAmountModal({ target, onCancel, onConfirm }) {
  if (!target) return null;
  return <SetAmountInner key={target.id} target={target} onCancel={onCancel} onConfirm={onConfirm} />;
}

function SetAmountInner({ target, onCancel, onConfirm }) {
  const projectedAbs = Math.abs(target.amount_cents);
  const [amountInput, setAmountInput] = useState((projectedAbs / 100).toFixed(2));

  function submit() {
    const cents = parseAmountToCents(amountInput);
    if (cents == null || cents <= 0) {
      // Invalid/blank → treat as a clear (restore projected)
      onConfirm(target, null);
      return;
    }
    onConfirm(target, cents);
  }

  function clearOverride() {
    onConfirm(target, null);
  }

  const entered = parseAmountToCents(amountInput);
  const differs = entered != null && entered !== projectedAbs;

  return (
    <Modal
      open={true}
      onClose={onCancel}
      title={`Adjust amount: ${target.item}`}
      footer={
        <>
          <button onClick={onCancel}>Cancel</button>
          <button className="primary" onClick={submit}>Save</button>
        </>
      }
    >
      <div className="muted tiny" style={{ marginBottom: 12 }}>
        {fmtDateShort(target.date)} · {target.type}
      </div>
      <div className="field">
        <label>Planned amount</label>
        <input
          inputMode="decimal"
          value={amountInput}
          onChange={(e) => setAmountInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          autoFocus
        />
        <div className="tiny muted" style={{ marginTop: 4 }}>
          Rule&apos;s projected: <span className="mono">{fmtCents(target.amount_cents)}</span>
          {differs && <> · <span style={{ color: 'var(--warn)' }}>Adjusted</span></>}
        </div>
      </div>
      <div className="tiny muted" style={{ marginBottom: 8 }}>
        This updates the forecast row only — your recurring rule stays at its original amount.
        When you mark this paid, the adjusted amount will pre-fill.
      </div>
      {target.adjusted && (
        <button className="ghost tiny" onClick={clearOverride} style={{ marginTop: 4 }}>
          Restore to projected amount
        </button>
      )}
    </Modal>
  );
}

/**
 * AddExpectedModal — quickly create a one-off expected forecast item without
 * navigating to the Recurring screen. Under the hood it's a OneTime
 * recurring rule with active=1 and starts_on=the chosen date. The forecast
 * engine handles OneTime by emitting the row on starts_on if it falls in
 * the queried window.
 */
function AddExpectedModal({ open, onClose, state, mutate, showToast, onAdded }) {
  if (!open) return null;
  return <AddExpectedInner onClose={onClose} state={state} mutate={mutate} showToast={showToast} onAdded={onAdded} />;
}

function AddExpectedInner({ onClose, state, mutate, showToast, onAdded }) {
  const checkingId = state.accounts.find(a => a.name === 'Checking')?.id ?? '';
  const incomeCat = state.categories.find(c => /income/i.test(c.name))?.id ?? '';
  const [form, setForm] = useState({
    item: '',
    type: 'Income',                // bonus default; user can flip
    amountInput: '',
    starts_on: todayIso(),
    account_id: checkingId,
    category_id: incomeCat,
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function save() {
    if (!form.item || !form.starts_on || !form.account_id || !form.category_id) {
      showToast({ kind: 'error', message: 'Description, date, account, and category are required.' });
      return;
    }
    const cents = parseAmountToCents(form.amountInput);
    if (cents == null || cents <= 0) {
      showToast({ kind: 'error', message: 'Amount must be greater than 0.' });
      return;
    }
    setSaving(true);
    try {
      await mutate.addRecurring({
        item: form.item.trim(),
        amount_cents: Math.abs(cents),
        frequency: 'OneTime',
        every_n: 1,
        anchor: null,
        months_active: 'All',
        account_id: form.account_id,
        category_id: form.category_id,
        type: form.type,
        starts_on: form.starts_on,
        ends_on: null,
        active: 1,
        notes: form.notes,
      });
      showToast({ kind: 'success', message: `Added: ${form.item} on ${form.starts_on}` });
      if (onAdded) await onAdded();
      onClose();
    } catch (e) {
      showToast({ kind: 'error', message: 'Failed: ' + e.message });
    } finally { setSaving(false); }
  }

  return (
    <Modal
      open={true}
      onClose={onClose}
      title="Add expected item"
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={save} disabled={saving}>
            {saving ? <span className="spinner" /> : 'Save'}
          </button>
        </>
      }
    >
      <div className="tiny muted" style={{ marginBottom: 12 }}>
        A one-off expected transaction. It shows up in the forecast so your
        runway projection includes it. Mark it paid when it actually happens.
      </div>

      <div className="field">
        <label>Description</label>
        <input
          value={form.item}
          onChange={(e) => update('item', e.target.value)}
          placeholder="e.g. Q2 bonus, tax refund, wedding gift"
          autoFocus
        />
      </div>

      <div className="row" style={{ gap: 8 }}>
        <div className="field" style={{ flex: 1 }}>
          <label>Type</label>
          <select value={form.type} onChange={(e) => update('type', e.target.value)}>
            <option>Income</option>
            <option>Bill</option>
            <option>Discretionary</option>
            <option>Other</option>
          </select>
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label>Amount</label>
          <input
            inputMode="decimal"
            placeholder="0.00"
            value={form.amountInput}
            onChange={(e) => update('amountInput', e.target.value)}
          />
        </div>
      </div>

      <div className="field">
        <label>Date</label>
        <input type="date" value={form.starts_on} onChange={(e) => update('starts_on', e.target.value)} />
      </div>

      <div className="row" style={{ gap: 8 }}>
        <div className="field" style={{ flex: 1 }}>
          <label>Category</label>
          <select value={form.category_id} onChange={(e) => update('category_id', e.target.value)}>
            <option value="">Select…</option>
            {state.categories.filter(c => !c.archived).map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label>Account</label>
          <select value={form.account_id} onChange={(e) => update('account_id', e.target.value)}>
            <option value="">Select…</option>
            {state.accounts.filter(a => !a.archived).map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="field">
        <label>Notes (optional)</label>
        <input value={form.notes} onChange={(e) => update('notes', e.target.value)} />
      </div>
    </Modal>
  );
}
